import { useRef, useEffect, useCallback, useState, useMemo } from "react";
import useSWR from 'swr';
import { validateUserOperationCallData } from "./types";
import { KERNEL_API_URL, RECOVERY_DASHBOARD_URL } from "../constants";
import { isAddress } from "../utils/isAddress";
const fetcher = async (url) => {
    const response = await fetch(url);
    return response.json();
};
const useKernelAccountRecovery = ({ address, onSetupGuardianRequest, chainId, suggestedGuardianAddress }) => {
    const childWindowRef = useRef(null);
    const [error, setError] = useState(undefined);
    const [isPending, setIsPending] = useState(false);
    const [popupCheckInterval, setPopupCheckInterval] = useState(null);
    const [requestType, setRequestType] = useState(undefined);
    const { data } = useSWR(address ? `${KERNEL_API_URL}/accounts/${address}/guardians?chainId=${chainId}` : null, fetcher, { refreshInterval: 3000 });
    const guardians = useMemo(() => {
        if (!data) {
            return [];
        }
        if (data.length > 0 && requestType === 'enableRecovery') {
            setIsPending(false);
            setRequestType(undefined);
        }
        if (data.length === 0 && requestType === 'deleteRecovery') {
            setIsPending(false);
            setRequestType(undefined);
        }
        return data.map((guardian) => guardian.guardian);
    }, [data]);
    const checkPopupWindow = useCallback(() => {
        if (childWindowRef.current && childWindowRef.current.closed) {
            setIsPending(false);
        }
    }, []);
    const openRecoveryPopup = useCallback(() => {
        if (address === undefined || chainId === undefined) {
            return;
        }
        setIsPending(true);
        const parentUrl = encodeURIComponent(window.location.origin);
        let dashboardUrl = `${RECOVERY_DASHBOARD_URL}/recovery-setup/${address}?parentUrl=${parentUrl}&chainId=${chainId}`;
        if (suggestedGuardianAddress && isAddress(suggestedGuardianAddress)) {
            dashboardUrl = dashboardUrl.concat(`&suggestedGuardianAddress=${suggestedGuardianAddress}`);
        }
        const windowFeatures = 'width=400,height=560,resizable,scrollbars=yes,status=1';
        childWindowRef.current = window.open(dashboardUrl, '_blank', windowFeatures);
        if (childWindowRef.current) {
            childWindowRef.current.focus();
            if (!popupCheckInterval) {
                const interval = setInterval(checkPopupWindow, 1000);
                setPopupCheckInterval(interval);
            }
        }
    }, [address, checkPopupWindow, popupCheckInterval, chainId]);
    useEffect(() => {
        if (popupCheckInterval && !isPending) {
            clearInterval(popupCheckInterval);
            setPopupCheckInterval(null);
        }
    }, [popupCheckInterval, isPending]);
    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.origin !== RECOVERY_DASHBOARD_URL) {
                return;
            }
            if (error) {
                setError(undefined);
            }
            const { userOp, request } = event.data;
            setRequestType(request);
            const parseUserOpCallData = validateUserOperationCallData(userOp);
            if (!parseUserOpCallData.success) {
                setError('Invalid user operation call data');
                return;
            }
            childWindowRef.current?.postMessage({
                type: 'tx-submitted',
                status: 'processing'
            }, RECOVERY_DASHBOARD_URL);
            if (onSetupGuardianRequest) {
                await onSetupGuardianRequest(parseUserOpCallData.data);
            }
            childWindowRef.current?.postMessage({
                type: 'tx-submitted',
                status: 'done'
            }, RECOVERY_DASHBOARD_URL);
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            if (popupCheckInterval) {
                clearInterval(popupCheckInterval);
            }
        };
    }, [onSetupGuardianRequest, popupCheckInterval]);
    return {
        openRecoveryPopup,
        error,
        recoveryEnabled: guardians.length > 0,
        guardians,
        isPending
    };
};
export default useKernelAccountRecovery;
